import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Shield, Zap, Users, ChevronLeft, ChevronRight, Brain, ClipboardList, BarChart3, HeartPulse } from 'lucide-react';

function CpuIcon(props) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <rect width="16" height="16" x="4" y="4" rx="2"/><rect width="6" height="6" x="9" y="9" rx="1"/>
      <path d="M15 2v2"/><path d="M15 20v2"/><path d="M2 15h2"/><path d="M2 9h2"/><path d="M20 15h2"/><path d="M20 9h2"/><path d="M9 2v2"/><path d="M9 20v2"/>
    </svg>
  );
}

const slides = [
  {
    badge: 'AI-Powered Care Management',
    badgeIcon: Zap,
    title: 'Care',
    titleGradient: 'Unlimited.',
    subtitle: 'One Platform. Everything Connected.',
    description: 'The all-in-one AI-powered care management platform that replaces your entire software stack. Built for domiciliary care, care homes, and nursing facilities across the UK.',
    accentColor: 'from-primary to-accent',
    mockup: 'dashboard',
  },
  {
    badge: 'Intelligent Care Planning',
    badgeIcon: Brain,
    title: 'Smarter',
    titleGradient: 'Care Plans.',
    subtitle: 'AI writes. You review. Residents benefit.',
    description: 'Our AI generates personalised care plans in seconds — pulling from assessment data, risk scores, and historical notes. Review, approve, and go.',
    accentColor: 'from-accent to-primary',
    mockup: 'careplans',
  },
  {
    badge: 'Real-Time Compliance',
    badgeIcon: ClipboardList,
    title: 'Always',
    titleGradient: 'CQC Ready.',
    subtitle: 'Live compliance — zero last-minute panic.',
    description: 'Real-time CQC compliance dashboards, automatic evidence collection, and instant alerts when standards drift. Walk into every inspection with confidence.',
    accentColor: 'from-chart-4 to-accent',
    mockup: 'compliance',
  },
  {
    badge: 'Analytics & Insights',
    badgeIcon: BarChart3,
    title: 'Data-Driven',
    titleGradient: 'Decisions.',
    subtitle: 'Know your care. Prove your quality.',
    description: 'From occupancy trends to staff performance and clinical outcomes — our analytics suite surfaces the insights that help you lead with confidence.',
    accentColor: 'from-primary to-chart-5',
    mockup: 'analytics',
  },
  {
    badge: 'AI Training Management',
    badgeIcon: Users,
    title: 'AI-Powered',
    titleGradient: 'Staff Training.',
    subtitle: 'Generate. Assign. Track. Certify.',
    description: 'Create mandatory and custom training courses with AI, assign them to any role, track completion in real time, send reminders, and award digital badges — all from one platform.',
    accentColor: 'from-accent to-chart-5',
    mockup: 'dashboard',
  },
  {
    badge: 'Archive & Compliance Records',
    badgeIcon: Shield,
    title: 'Secure',
    titleGradient: 'Audit & Archive.',
    subtitle: '5-Year archive. Full audit history. Always.',
    description: 'Retain staff, resident, and branch records for up to 5 years with one-click restore. Every action is logged in a detailed module-wise audit trail — ready for CQC or legal review.',
    accentColor: 'from-chart-4 to-primary',
    mockup: 'compliance',
  },
];

function DashboardMockup() {
  return (
    <div className="relative bg-card/90 backdrop-blur-sm border border-border/50 rounded-2xl shadow-2xl overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border/30 bg-muted/30">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-destructive/60" />
          <div className="w-2.5 h-2.5 rounded-full bg-chart-4/60" />
          <div className="w-2.5 h-2.5 rounded-full bg-accent/60" />
        </div>
        <div className="flex-1 mx-6"><div className="bg-muted/50 rounded-md h-4 max-w-xs mx-auto" /></div>
      </div>
      <div className="p-4 space-y-3">
        <div className="grid grid-cols-3 gap-2.5">
          {[
            { label: 'Active Residents', value: '247', color: 'from-primary/20 to-primary/5' },
            { label: 'Staff On Duty', value: '52', color: 'from-accent/20 to-accent/5' },
            { label: 'Tasks Today', value: '18', color: 'from-chart-4/20 to-chart-4/5' },
          ].map((s) => (
            <div key={s.label} className={`bg-gradient-to-br ${s.color} rounded-xl p-3 border border-border/20`}>
              <p className="text-xs text-muted-foreground">{s.label}</p>
              <p className="text-lg font-bold font-heading text-foreground mt-0.5">{s.value}</p>
            </div>
          ))}
        </div>
        <div className="bg-muted/30 rounded-xl p-3 border border-border/20">
          <p className="text-xs font-medium text-foreground mb-2">Care Activity Overview</p>
          <div className="flex items-end gap-1 h-16">
            {[40, 65, 45, 80, 55, 70, 90, 60, 75, 85, 50, 95].map((h, i) => (
              <motion.div key={i} initial={{ height: 0 }} animate={{ height: `${h}%` }}
                transition={{ delay: 0.6 + i * 0.04, duration: 0.4 }}
                className={`flex-1 rounded-sm ${i % 2 === 0 ? 'bg-primary/60' : 'bg-accent/40'}`} />
            ))}
          </div>
        </div>
        <div className="space-y-1.5">
          {['Medication Round – Wing A', 'Staff Handover Due', 'New Care Plan Review'].map((item, i) => (
            <div key={i} className="flex items-center gap-2.5 bg-muted/20 rounded-lg px-3 py-1.5 border border-border/10">
              <div className={`w-1.5 h-1.5 rounded-full ${['bg-primary', 'bg-chart-4', 'bg-accent'][i]}`} />
              <span className="text-xs text-foreground flex-1">{item}</span>
              <span className="text-xs text-muted-foreground">{['Now', '2:30 PM', '3:00 PM'][i]}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function CarePlansMockup() {
  return (
    <div className="relative bg-card/90 backdrop-blur-sm border border-border/50 rounded-2xl shadow-2xl overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border/30 bg-muted/30">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-destructive/60" />
          <div className="w-2.5 h-2.5 rounded-full bg-chart-4/60" />
          <div className="w-2.5 h-2.5 rounded-full bg-accent/60" />
        </div>
        <span className="text-xs text-muted-foreground ml-2 font-medium">AI Care Planner</span>
      </div>
      <div className="p-4 space-y-3">
        <div className="flex items-center gap-3 bg-accent/10 border border-accent/20 rounded-xl p-3">
          <Brain className="w-5 h-5 text-accent flex-shrink-0" />
          <div>
            <p className="text-xs font-semibold text-foreground">AI generating care plan…</p>
            <p className="text-xs text-muted-foreground">Margaret T. · Risk: Low · Age: 84</p>
          </div>
          <div className="ml-auto flex gap-1">
            {[0,1,2].map(i => (
              <div key={i} className="w-1.5 h-1.5 rounded-full bg-accent pch-pulse-dot"
                style={{ animationDelay: `${i * 0.3}s` }} />
            ))}
          </div>
        </div>
        {['Personal Care & Hygiene', 'Nutrition & Hydration', 'Mobility & Falls Prevention', 'Medication Management'].map((section, i) => (
          <motion.div key={section} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 + i * 0.15 }}
            className="flex items-center gap-3 bg-muted/20 border border-border/20 rounded-lg px-3 py-2">
            <div className="w-5 h-5 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-bold text-accent">{i + 1}</span>
            </div>
            <span className="text-xs text-foreground">{section}</span>
            <motion.div initial={{ width: 0 }} animate={{ width: '100%' }} transition={{ delay: 0.5 + i * 0.2, duration: 0.6 }}
              className="ml-auto h-1 rounded-full bg-accent/30 max-w-16" />
          </motion.div>
        ))}
        <div className="flex gap-2 pt-1">
          <button className="flex-1 text-xs py-2 rounded-lg bg-accent text-white font-medium">Approve Plan</button>
          <button className="flex-1 text-xs py-2 rounded-lg bg-muted border border-border/40 text-muted-foreground">Edit</button>
        </div>
      </div>
    </div>
  );
}

function ComplianceMockup() {
  const items = [
    { label: 'Safe', score: 96, color: 'bg-accent' },
    { label: 'Effective', score: 94, color: 'bg-primary' },
    { label: 'Caring', score: 99, color: 'bg-chart-4' },
    { label: 'Responsive', score: 91, color: 'bg-chart-5' },
    { label: 'Well-Led', score: 97, color: 'bg-accent' },
  ];
  return (
    <div className="relative bg-card/90 backdrop-blur-sm border border-border/50 rounded-2xl shadow-2xl overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border/30 bg-muted/30">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-destructive/60" />
          <div className="w-2.5 h-2.5 rounded-full bg-chart-4/60" />
          <div className="w-2.5 h-2.5 rounded-full bg-accent/60" />
        </div>
        <span className="text-xs text-muted-foreground ml-2 font-medium">CQC Compliance Dashboard</span>
      </div>
      <div className="p-4 space-y-3">
        <div className="flex items-center justify-between bg-accent/10 border border-accent/20 rounded-xl p-3">
          <div>
            <p className="text-xs font-semibold text-foreground">Overall Rating</p>
            <p className="text-xs text-muted-foreground">Last inspection: 14 Mar 2026</p>
          </div>
          <span className="text-lg font-extrabold font-heading text-accent">Outstanding</span>
        </div>
        <div className="space-y-2.5">
          {items.map((item, i) => (
            <div key={item.label}>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-foreground font-medium">{item.label}</span>
                <span className="text-muted-foreground">{item.score}%</span>
              </div>
              <div className="h-2 bg-muted/40 rounded-full overflow-hidden">
                <motion.div initial={{ width: 0 }} animate={{ width: `${item.score}%` }}
                  transition={{ delay: 0.3 + i * 0.1, duration: 0.7, ease: 'easeOut' }}
                  className={`h-full rounded-full ${item.color}`} />
              </div>
            </div>
          ))}
        </div>
        <div className="flex gap-2 text-xs text-muted-foreground bg-muted/20 rounded-lg p-2.5 border border-border/20">
          <Shield className="w-3.5 h-3.5 text-accent flex-shrink-0 mt-0.5" />
          <span>All 42 evidence items collected. Ready for inspection.</span>
        </div>
      </div>
    </div>
  );
}

function AnalyticsMockup() {
  return (
    <div className="relative bg-card/90 backdrop-blur-sm border border-border/50 rounded-2xl shadow-2xl overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border/30 bg-muted/30">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-destructive/60" />
          <div className="w-2.5 h-2.5 rounded-full bg-chart-4/60" />
          <div className="w-2.5 h-2.5 rounded-full bg-accent/60" />
        </div>
        <span className="text-xs text-muted-foreground ml-2 font-medium">Analytics & Insights</span>
      </div>
      <div className="p-4 space-y-3">
        <div className="grid grid-cols-2 gap-2.5">
          {[
            { label: 'Occupancy Rate', value: '94%', trend: '+2.1%', up: true, color: 'from-primary/20 to-primary/5' },
            { label: 'Avg Care Hours', value: '6.4h', trend: '+0.3h', up: true, color: 'from-accent/20 to-accent/5' },
            { label: 'Agency Spend', value: '£12k', trend: '-18%', up: false, color: 'from-chart-4/20 to-chart-4/5' },
            { label: 'Incidents MTD', value: '3', trend: '-5', up: false, color: 'from-chart-5/20 to-chart-5/5' },
          ].map((s) => (
            <div key={s.label} className={`bg-gradient-to-br ${s.color} rounded-xl p-3 border border-border/20`}>
              <p className="text-xs text-muted-foreground">{s.label}</p>
              <p className="text-base font-bold font-heading text-foreground mt-0.5">{s.value}</p>
              <p className={`text-xs font-medium ${s.up ? 'text-accent' : 'text-primary'}`}>{s.trend} this month</p>
            </div>
          ))}
        </div>
        <div className="bg-muted/30 rounded-xl p-3 border border-border/20">
          <p className="text-xs font-medium text-foreground mb-2">Occupancy – Last 8 Weeks</p>
          <div className="flex items-end gap-1.5 h-14">
            {[78, 82, 85, 80, 88, 91, 90, 94].map((h, i) => (
              <motion.div key={i} initial={{ height: 0 }} animate={{ height: `${h}%` }}
                transition={{ delay: 0.4 + i * 0.06, duration: 0.5 }}
                className="flex-1 rounded-sm bg-primary/60" />
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs bg-primary/5 border border-primary/20 rounded-lg p-2.5">
          <HeartPulse className="w-3.5 h-3.5 text-primary flex-shrink-0" />
          <span className="text-muted-foreground">AI Insight: <strong className="text-foreground">Occupancy up 4% — consider capacity expansion in Q3.</strong></span>
        </div>
      </div>
    </div>
  );
}

const MOCKUPS = [DashboardMockup, CarePlansMockup, ComplianceMockup, AnalyticsMockup, DashboardMockup, ComplianceMockup];

const slideVariants = {
  enter: (dir) => ({ opacity: 0, x: dir > 0 ? 60 : -60 }),
  center: { opacity: 1, x: 0 },
  exit: (dir) => ({ opacity: 0, x: dir > 0 ? -60 : 60 }),
};

const contentVariants = {
  enter: (dir) => ({ opacity: 0, y: dir > 0 ? 20 : -20 }),
  center: { opacity: 1, y: 0 },
  exit: (dir) => ({ opacity: 0, y: dir > 0 ? -20 : 20 }),
};

export default function HeroSection() {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(1);

  const go = useCallback((idx) => {
    setDirection(idx > current ? 1 : -1);
    setCurrent(idx);
  }, [current]);

  const next = useCallback(() => {
    setDirection(1);
    setCurrent((c) => (c + 1) % slides.length);
  }, []);

  const prev = useCallback(() => {
    setDirection(-1);
    setCurrent((c) => (c - 1 + slides.length) % slides.length);
  }, []);

  useEffect(() => {
    const t = setTimeout(next, 11000);
    return () => clearTimeout(t);
  }, [current, next]);

  const slide = slides[current];
  const BadgeIcon = slide.badgeIcon;
  const MockupComponent = MOCKUPS[current];

  return (
    <section id="hero" className="relative min-h-[80vh] flex items-center overflow-hidden pt-20 lg:pt-32 pb-16 lg:pb-24">
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-pulse-glow" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-accent/10 rounded-full blur-3xl animate-pulse-glow" style={{ animationDelay: '1.5s' }} />
        <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05]"
          style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)', backgroundSize: '40px 40px' }} />
      </div>

      <div className="max-w-screen-2xl mx-auto px-4 sm:px-8 lg:px-16 relative z-10 w-full">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Left — animated text content */}
          <div className="relative">
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={current}
                custom={direction}
                variants={contentVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.45, ease: 'easeInOut' }}
              >
                <div className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium mb-5`}>
                  <BadgeIcon className="w-3.5 h-3.5" />
                  {slide.badge}
                </div>

                <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.06] tracking-tight text-foreground">
                  {slide.title}{' '}
                  <span className={`bg-gradient-to-r ${slide.accentColor} bg-clip-text text-transparent`}>
                    {slide.titleGradient}
                  </span>
                  <br />
                  <span className="text-2xl sm:text-3xl lg:text-4xl text-muted-foreground font-bold leading-snug">
                    {slide.subtitle}
                  </span>
                </h1>

                <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
                  {slide.description}
                </p>

                <div className="mt-7 flex flex-wrap gap-3">
                  <Link to="/contact">
                    <Button size="lg" className={`text-base px-7 py-5 bg-gradient-to-r ${slide.accentColor} hover:opacity-90 shadow-xl shadow-primary/25 transition-all`}>
                      Get Demo <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                  <Link to="/contact">
                    <Button size="lg" variant="outline" className="text-base px-7 py-5 border-border/60 hover:bg-muted/50">
                      Start Free Trial
                    </Button>
                  </Link>
                </div>

                <div className="mt-8 flex flex-wrap items-center gap-5 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2"><Shield className="w-4 h-4 text-accent" /><span>CQC Compliant</span></div>
                  <div className="flex items-center gap-2"><Zap className="w-4 h-4 text-primary" /><span>AI-Powered</span></div>
                  <div className="flex items-center gap-2"><Users className="w-4 h-4 text-chart-4" /><span>1-Week Free Trial</span></div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Slide nav dots + arrows */}
            <div className="mt-10 flex items-center gap-4">
              <button onClick={prev} aria-label="Previous slide" className="w-11 h-11 rounded-full bg-muted/60 border border-border/40 flex items-center justify-center hover:bg-muted transition-colors">
                <ChevronLeft className="w-4 h-4 text-foreground" />
              </button>
              <div className="flex gap-2">
                {slides.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => go(i)}
                    aria-label={`Go to slide ${i + 1}`}
                    aria-current={i === current ? 'true' : undefined}
                    className="w-6 h-6 flex items-center justify-center rounded-full"
                  >
                    <span className={`block rounded-full transition-all duration-300 ${i === current ? 'w-5 h-2.5 bg-primary' : 'w-2.5 h-2.5 bg-muted-foreground/40 hover:bg-muted-foreground/70'}`} />
                  </button>
                ))}
              </div>
              <button onClick={next} aria-label="Next slide" className="w-11 h-11 rounded-full bg-muted/60 border border-border/40 flex items-center justify-center hover:bg-muted transition-colors">
                <ChevronRight className="w-4 h-4 text-foreground" />
              </button>
              <span className="text-xs text-muted-foreground ml-1">{current + 1} / {slides.length}</span>
            </div>
          </div>

          {/* Right — animated mockup */}
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 via-accent/10 to-primary/20 rounded-3xl blur-2xl opacity-50" />
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={current}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.45, ease: 'easeInOut' }}
                className="relative"
              >
                <MockupComponent />

                {/* Floating badges */}
                <div
                  className="absolute -top-4 -right-3 bg-card border border-border/50 rounded-xl px-3 py-2 shadow-xl pch-float-1"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-accent/20 flex items-center justify-center">
                      <Shield className="w-3 h-3 text-accent" />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-foreground">CQC Score</p>
                      <p className="text-xs font-bold text-accent">Outstanding</p>
                    </div>
                  </div>
                </div>

                <div
                  className="absolute -bottom-3 -left-3 bg-card border border-border/50 rounded-xl px-3 py-2 shadow-xl pch-float-2"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center">
                      <CpuIcon className="w-3 h-3 text-primary" />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-foreground">AI Active</p>
                      <p className="text-xs text-primary font-medium">Monitoring</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}